import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Grid,
  useTheme,
  Avatar,
  Divider
} from '@mui/material';
import {
  BugReport,
  Schedule,
  CheckCircle,
  Cancel,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const UserTicketTracker = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = async () => {
    if (!user?.email) return;
    
    try {
      const response = await fetch(`/api/support/tickets/${user.email}`);
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [user?.email]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return '#ff9800';
      case 'in-progress': return '#2196f3';
      case 'resolved': return '#4caf50';
      case 'closed': return '#9e9e9e';
      default: return '#ff9800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Schedule />;
      case 'in-progress': return <BugReport />;
      case 'resolved': return <CheckCircle />;
      case 'closed': return <Cancel />;
      default: return <Schedule />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'high': return '#f44336';
      case 'urgent': return '#9c27b0';
      default: return '#ff9800';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading tickets...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            background: isDark
              ? 'linear-gradient(45deg, #FFD700, #FFA500)'
              : 'linear-gradient(45deg, #3B82F6, #93C5FD)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          My Support Tickets
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchTickets}
        >
          Refresh
        </Button>
      </Box>

      {tickets.length === 0 ? (
        <Card
          sx={{
            background: isDark
              ? 'rgba(0, 0, 0, 0.3)'
              : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            textAlign: 'center',
            py: 6
          }}
        >
          <CardContent>
            <Typography variant="h6" color="text.secondary">
              No support tickets found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create your first support ticket to get help from our team
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {tickets.map((ticket) => (
            <Grid item xs={12} key={ticket.id}>
              <Card
                sx={{
                  background: isDark
                    ? 'rgba(0, 0, 0, 0.3)'
                    : 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {ticket.subject}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Ticket ID: {ticket.ticketId}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        icon={getStatusIcon(ticket.status)}
                        label={ticket.status.replace('-', ' ').toUpperCase()}
                        sx={{
                          bgcolor: getStatusColor(ticket.status),
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                      <Chip
                        label={ticket.priority.toUpperCase()}
                        sx={{
                          bgcolor: getPriorityColor(ticket.priority),
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {ticket.message}
                  </Typography>

                  {ticket.screenshotUrl && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Attachment:
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => window.open(ticket.screenshotUrl, '_blank')}
                      >
                        View Screenshot
                      </Button>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Created: {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated: {new Date(ticket.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {ticket.assignedTo && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Assigned to: {ticket.assignedTo}
                    </Typography>
                  )}

                  {ticket.resolution && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Resolution:
                      </Typography>
                      <Typography variant="body2">
                        {ticket.resolution}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default UserTicketTracker;